import { useConsoleStore, type MessageLevel } from '@/state/consoleStore';
import { SKYROVER_COLORS } from '@/theme/skyroverTheme';
import { useUploadStore } from '@/state/uploadStore';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';

const levelColors: Record<MessageLevel, { bg: string; border: string }> = {
  success: { bg: '#E8F5E9', border: '#A5D6A7' },
  info: { bg: '#F9E6F9', border: '#D966D9' },
  warning: { bg: '#FFF8E1', border: '#FFE082' },
  error: { bg: '#FFEBEE', border: '#EF9A9A' },
};

function formatUploadProgressLabel(status: string, progress: number): string {
  const cleanStatus = status.replace(/\s*\d+(?:\.\d+)?%\s*$/, '').trim();
  const roundedProgress = Math.round(progress);

  if (!cleanStatus) {
    return `${roundedProgress}%`;
  }

  return `${cleanStatus} ${roundedProgress}%`;
}

export function MessagesPanel() {
  const messages = useConsoleStore((s) => s.messages);
  const uploading = useUploadStore((s) => s.uploading);
  const uploadProgress = useUploadStore((s) => s.uploadProgress);
  const uploadStatus = useUploadStore((s) => s.uploadStatus);
  const scrollRef = useRef<HTMLDivElement>(null);
  const latestMessages = useMemo(() => [...messages].reverse(), [messages]);

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.scrollTop = 0;
    }
  }, [messages]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {latestMessages.map((msg) => {
          const colors = levelColors[msg.level];
          return (
            <Box
              key={msg.id}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: colors.bg,
                border: '1px solid',
                borderColor: colors.border,
              }}
            >
              <Typography variant="body2" sx={{ fontSize: 12, lineHeight: 1.5 }}>
                {msg.text}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {uploading ? (
        <Box
          sx={{
            flexShrink: 0,
            position: 'sticky',
            bottom: 0,
            px: 1.5,
            py: 1,
            bgcolor: '#fff',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.06)',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
          >
            {formatUploadProgressLabel(uploadStatus, uploadProgress)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: SKYROVER_COLORS.brandMuted,
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                transition: 'transform 0.25s linear',
                bgcolor: SKYROVER_COLORS.brand,
              },
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}
